<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-800/60 p-5" @click.self="close" @keydown.esc.stop.prevent="close" @keydown.tab="trapFocus">
      <div ref="dialog" role="dialog" aria-modal="true" aria-label="Add identifier" class="w-full max-w-xl overflow-hidden rounded-lg border border-gray-200 bg-white text-[11px] leading-4 shadow-xl">
        <div class="flex items-start justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
          <div class="min-w-0">
            <h3 class="text-xs font-semibold text-gray-900">Add identifier</h3>
            <p class="sensitive mt-1 break-words text-gray-600">{{ entity.name }}</p>
          </div>
          <button type="button" class="identifier-button !w-6 !p-0" aria-label="Close add-identifier dialog" :disabled="saving" @click="close">
            <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" d="M6 6l12 12M6 18L18 6" /></svg>
          </button>
        </div>
        <div class="p-4">
          <LoadingIndicator v-if="loading" class="!p-2" message="Loading identifier types…" />
          <form v-else class="flex flex-col gap-2 sm:flex-row sm:items-end" @submit.prevent="save">
            <label class="flex min-w-0 flex-col gap-1 text-gray-600 sm:w-36 sm:shrink-0">
              Identifier type
              <select ref="typeInput" v-model="identifierTypeId" class="identifier-input" :disabled="saving || !availableTypes.length" required>
                <option :value="null">Select type…</option>
                <option v-for="type in availableTypes" :key="type.id" :value="type.id">{{ type.type_name }}</option>
              </select>
            </label>
            <label class="flex min-w-0 flex-1 flex-col gap-1 text-gray-600">
              Value
              <input v-model="value" type="text" class="sensitive identifier-input" placeholder="Enter identifier…" :disabled="saving" required />
            </label>
            <button type="submit" class="identifier-button identifier-primary shrink-0 self-start sm:self-auto" :disabled="saving || !identifierTypeId || !value.trim()">{{ saving ? 'Adding…' : 'Add' }}</button>
          </form>
          <p v-if="error" role="alert" class="sensitive mt-2 text-red-700" data-demo-blur>{{ error }}</p>
          <p v-else-if="!loading && !availableTypes.length" class="mt-2 text-gray-500">No unused identifier types are available.</p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import axios from 'axios';
import abortable from '@/mixins/abortable';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'AddEntityIdentifierModal',
  components: { LoadingIndicator },
  mixins: [abortable],
  props: { entity: { type: Object, required: true } },
  emits: ['created', 'close'],
  data() {
    return { types: [], identifierTypeId: null, value: '', loading: true, saving: false, error: '' };
  },
  computed: {
    availableTypes() {
      const used = new Set((this.entity.identifiers || []).map(identifier => identifier.identifier_type));
      return this.types.filter(type => !used.has(type.id));
    },
  },
  async mounted() {
    this._returnFocus = document.activeElement;
    this._bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const signal = this._renewAbortSignal();
    try {
      const { data } = await axios.get('/api/identifiertypes/', { signal });
      if (!signal.aborted) this.types = data;
    } catch (error) {
      if (!this._isAborted(error)) this.error = 'Could not load identifier types.';
    } finally {
      if (!signal.aborted) {
        this.loading = false;
        await this.$nextTick();
        this.$refs.typeInput?.focus();
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = this._bodyOverflow || '';
    if (this._returnFocus?.isConnected) this._returnFocus.focus();
  },
  methods: {
    close() {
      if (!this.saving) this.$emit('close');
    },
    trapFocus(event) {
      const elements = [...this.$refs.dialog.querySelectorAll('button:not(:disabled), select:not(:disabled), input:not(:disabled)')];
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    },
    async save() {
      if (this.saving || !this.identifierTypeId || !this.value.trim()) return;
      this.saving = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/identifiers/', {
          entity: this.entity.id,
          identifier_type: this.identifierTypeId,
          identifier: this.value.trim(),
        });
        this.$emit('created', data);
        this.$emit('close');
      } catch (error) {
        this.error = error.response?.data?.identifier?.[0] || error.response?.data?.non_field_errors?.[0] || error.response?.data?.detail || 'Could not add identifier.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
@reference "../../assets/theme.css";
.identifier-button {
  @apply inline-flex h-6 items-center justify-center rounded border border-gray-300 bg-white px-2 text-[11px] font-medium leading-4 text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500 disabled:opacity-50;
}
.identifier-primary {
  @apply border-primary-500 bg-primary-500 text-white hover:bg-primary-600;
}
.identifier-input {
  @apply h-6 w-full rounded border border-gray-300 bg-white px-2 py-0 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-neutral-500 disabled:opacity-50;
}
</style>
